import React, { useRef, useState } from 'react';
import { createVault, validatePassword } from './walletVault';

// TheoWallet: створення Solana-гаманця одним захищеним процесом.
// Порядок: пароль -> ключ генерується на пристрої -> одразу шифрується (сховище перевіряється)
// -> адреса записується на сервер (якщо не вдалось — сховище видаляється, нічого не створено)
// -> бекап (завантажити / скопіювати) + підтвердження -> готово.
// Відкритий ключ ніде не зберігається: лише в пам'яті цього вікна до натискання "Done".

const API = 'https://orkxz4jpah.execute-api.us-east-1.amazonaws.com/update-profile';
const VAULT_PREFIX = 'genesis_vault_';

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' };
const card = { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '420px', maxHeight: '92vh', overflowY: 'auto', padding: '22px', textAlign: 'left', boxSizing: 'border-box' };
const input = { width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', marginBottom: '10px', boxSizing: 'border-box' };
const primary = (enabled) => ({ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', background: enabled ? '#9945FF' : '#c9b6f2', color: '#fff', fontWeight: 700, fontSize: '15px', cursor: enabled ? 'pointer' : 'not-allowed' });
const secondary = { flex: 1, padding: '12px', borderRadius: '10px', border: '1.5px solid #1e293b', background: '#fff', color: '#1e293b', fontWeight: 600, cursor: 'pointer' };
const linkBtn = { background: 'none', border: 'none', color: '#4f46e5', fontSize: '13px', cursor: 'pointer', padding: 0 };

function CreateWalletModal({ genesisID, onCreated, onClose }) {
  const [step, setStep] = useState('password'); // 'password' | 'backup'
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [address, setAddress] = useState('');
  const [saved, setSaved] = useState({ download: false, copy: false });
  const [confirmed, setConfirmed] = useState(false);
  const backupRef = useRef(''); // текст бекапу живе лише в пам'яті

  const create = async () => {
    setError('');
    const pwErr = validatePassword(pw1);
    if (pwErr) { setError(pwErr); return; }
    if (pw1 !== pw2) { setError('Passwords do not match'); return; }
    setBusy(true);
    let newAddress = '';
    try {
      const { Keypair } = await import('@solana/web3.js');
      const kp = Keypair.generate();
      newAddress = kp.publicKey.toBase58();
      const secret = JSON.stringify(Array.from(kp.secretKey));
      const backup = `Genesis Solana Wallet Backup\nAddress: ${newAddress}\nSecret Key: ${secret}\nKEEP THIS SAFE!`;

      // 1. Шифруємо на пристрої (createVault сам перевіряє розшифруванням)
      await createVault(backup, pw1, newAddress);

      // 2. Записуємо адресу в профіль
      let ok = false;
      let serverError = '';
      try {
        const res = await fetch(API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ genesisID, solanaAddress: newAddress }),
        });
        const r = await res.json();
        ok = !!r.success;
        serverError = r.error || '';
      } catch (e) {
        serverError = 'Network error';
      }
      if (!ok) {
        try { localStorage.removeItem(VAULT_PREFIX + newAddress); } catch { /* ignore */ }
        throw new Error(`Could not save the wallet to your profile (${serverError || 'server error'}). Nothing was created. Please try again.`);
      }

      backupRef.current = backup;
      setAddress(newAddress);
      setPw1(''); setPw2('');
      setStep('backup');
    } catch (e) {
      setError(e.message || 'Wallet creation failed. Nothing was created.');
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const blob = new Blob([backupRef.current], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `genesis-solana-${address.slice(0, 8)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setSaved((s) => ({ ...s, download: true }));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(backupRef.current);
      setSaved((s) => ({ ...s, copy: true }));
    } catch {
      setError('Copy failed. Please use "Download backup".');
    }
  };

  const finish = () => {
    if (saved.copy) { try { navigator.clipboard.writeText(''); } catch { /* ignore */ } }
    backupRef.current = '';
    onCreated(address);
  };

  const canFinish = confirmed && (saved.download || saved.copy);

  return (
    <div style={overlay}>
      <div style={card} role="dialog" aria-modal="true" aria-label="Create TheoWallet">
        {step === 'password' && (
          <>
            <h3 style={{ margin: '0 0 6px', fontSize: '20px' }}>Create your TheoWallet</h3>
            <p style={{ fontSize: '14px', color: '#555', margin: '0 0 16px' }}>
              Your wallet key is created and encrypted on this device. Genesis never receives it.
              Set a wallet password — you will use it (or Face ID) to send and swap.
            </p>
            {error && <div style={{ background: '#fdecea', color: '#b42318', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', marginBottom: '12px' }}>{error}</div>}
            <input style={input} type={showPw ? 'text' : 'password'} placeholder="Wallet password" value={pw1}
              onChange={(e) => setPw1(e.target.value)} autoComplete="new-password" disabled={busy} />
            <input style={input} type={showPw ? 'text' : 'password'} placeholder="Repeat wallet password" value={pw2}
              onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" disabled={busy} />
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '0 0 16px' }}>
              <span style={{ fontSize: '12px', color: '#888' }}>Min 8 characters, letters and digits</span>
              <button type="button" style={linkBtn} onClick={() => setShowPw(!showPw)}>{showPw ? '🙈 Hide' : '👁 Show'}</button>
            </div>
            <button style={primary(!busy && pw1 && pw2)} disabled={busy || !pw1 || !pw2} onClick={create}>
              {busy ? 'Creating wallet...' : 'Create wallet'}
            </button>
            <button type="button" onClick={onClose} disabled={busy}
              style={{ ...linkBtn, color: '#888', display: 'block', margin: '12px auto 0' }}>Cancel</button>
          </>
        )}

        {step === 'backup' && (
          <>
            <h3 style={{ margin: '0 0 6px', fontSize: '20px' }}>✅ Wallet created</h3>
            <p style={{ fontFamily: 'monospace', fontSize: '13px', background: '#f5f5f5', padding: '8px 10px', borderRadius: '8px', wordBreak: 'break-all', margin: '0 0 14px' }}>
              {address}
            </p>
            <div style={{ background: '#fff7e0', border: '1px solid #f5d28a', color: '#7a4a00', padding: '12px', borderRadius: '10px', fontSize: '14px', marginBottom: '14px' }}>
              <strong>Save your backup now.</strong> It is the only way to restore this wallet if you lose this device,
              clear the browser or forget the wallet password. Genesis cannot recover it.
              Keep it private — anyone with this file controls your funds.
            </div>
            {error && <div style={{ color: '#b42318', fontSize: '14px', marginBottom: '10px' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
              <button style={secondary} onClick={download}>{saved.download ? '✓ Downloaded' : '⬇ Download backup'}</button>
              <button style={secondary} onClick={copy}>{saved.copy ? '✓ Copied' : '📋 Copy backup'}</button>
            </div>
            <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '14px', color: '#333', marginBottom: '16px', cursor: 'pointer' }}>
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: '3px', flexShrink: 0 }} />
              <span>I saved my backup in a safe place (password manager, secure note or offline storage).</span>
            </label>
            <button style={primary(canFinish)} disabled={!canFinish} onClick={finish}>Done</button>
            {!canFinish && (
              <p style={{ fontSize: '12px', color: '#888', textAlign: 'center', margin: '8px 0 0' }}>
                Download or copy the backup and confirm to continue
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CreateWalletModal;
